import { Link, NavLink } from 'react-router-dom';
import { RenderItemProps, RenderSubItemProps } from './core/Navigation';
import { createRenderers, ElementCreators } from './core/renderFactory';

export const ReactRouterRenderItem = (props: RenderItemProps) => {
  const reactRouterElementCreators: ElementCreators = {
    createMainElement: ({ containerProps, accessibilityProps, onClick, onKeyDown, content, isDisabled }) => {
      const { item, hasSubItems } = props;

      // external redirect - use button
      if (item.external || item.externalRedirectKey) {
        return (
          <button
            {...containerProps}
            type="button"
            tabIndex={isDisabled ? -1 : 0}
            aria-disabled={isDisabled}
            onClick={onClick}
            onKeyDown={onKeyDown}
            disabled={isDisabled}
          >
            {content}
          </button>
        );
      }

      // has sub-items - use NavLink to first sub-item
      if (hasSubItems && item.subItems) {
        const firstEnabledSubItem = item.subItems.find((subItem) => !subItem.disabled && subItem.path);

        if (firstEnabledSubItem?.path) {
          return (
            <NavLink
              to={firstEnabledSubItem.path}
              className={containerProps.className as string}
              onClick={onClick}
              {...item.additionalAttributes}
              {...(item.intercomTarget && { 'data-intercom-target': item.intercomTarget })}
            >
              {({ isActive: _navLinkIsActive }) => content}
            </NavLink>
          );
        }

        // fallback to button if no enabled sub-items with paths
        return (
          <button
            {...containerProps}
            type="button"
            tabIndex={isDisabled ? -1 : 0}
            aria-disabled={isDisabled}
            onClick={onClick}
            onKeyDown={onKeyDown}
            disabled={isDisabled}
          >
            {content}
          </button>
        );
      }

      // internal navigation - use NavLink
      if (item.path) {
        return (
          <NavLink
            to={item.path}
            className={containerProps.className as string}
            onClick={onClick}
            {...item.additionalAttributes}
            {...(item.intercomTarget && { 'data-intercom-target': item.intercomTarget })}
          >
            {({ isActive: _navLinkIsActive }) => content}
          </NavLink>
        );
      }

      // disabled item - use div
      return (
        <div {...containerProps} {...accessibilityProps}>
          {content}
        </div>
      );
    },
    createSubElement: ({ item, className, isDisabled, onClick, onKeyDown }) =>
      item.path && !isDisabled ? (
        <Link
          key={item.id}
          to={item.path}
          className={className}
          onClick={onClick}
          {...(item.intercomTarget && { 'data-intercom-target': item.intercomTarget })}
        >
          {item.label}
        </Link>
      ) : (
        <button
          key={item.id}
          type="button"
          onClick={onClick}
          onKeyDown={onKeyDown}
          className={className}
          disabled={isDisabled}
          aria-disabled={isDisabled}
          tabIndex={isDisabled ? -1 : 0}
        >
          {item.label}
        </button>
      ),
  };

  const { renderItem } = createRenderers(reactRouterElementCreators);
  return renderItem(props);
};

export const ReactRouterRenderSubItem = (props: RenderSubItemProps) => {
  const reactRouterElementCreators: ElementCreators = {
    createMainElement: () => <div />, // not used for sub items
    createSubElement: ({ item, className, isDisabled, onClick, onKeyDown }) =>
      item.path && !isDisabled ? (
        <Link
          key={item.id}
          to={item.path}
          className={className}
          onClick={onClick}
          {...(item.intercomTarget && { 'data-intercom-target': item.intercomTarget })}
        >
          {item.label}
        </Link>
      ) : (
        <button
          key={item.id}
          type="button"
          onClick={onClick}
          onKeyDown={onKeyDown}
          className={className}
          disabled={isDisabled}
          aria-disabled={isDisabled}
          tabIndex={isDisabled ? -1 : 0}
        >
          {item.label}
        </button>
      ),
  };

  const { renderSubItem } = createRenderers(reactRouterElementCreators);
  return renderSubItem(props);
};

export const useReactRouterRenderProps = () => ({
  renderItem: ReactRouterRenderItem,
  renderSubItem: ReactRouterRenderSubItem,
});
